// lib/opencv-worker-client.ts
import type { CV } from '@techstark/opencv-js';
import { computeAutoCutExtraction, type AutoCutExtraction, type AutoCutGeometry } from '#lib/auto-cut';
import { harmonizeLayerDrift, type DriftHarmonizationJob } from '#lib/drift-harmonization';
import { computeTiledLayerAlignment } from '#lib/opencv-tiled-alignment';
import { copyPixels, decodeWorkerPixels, runWithWorkerFallback, sendWorkerRequest, type WirePixels } from '#lib/worker-rpc';
import type { LayerAlignmentResult, LazyWorkerRef, OpenCvReadyMessage } from '#types/types';

type OpenCvJob =
  | { kind: 'layerAlignment'; edited: WirePixels; original: WirePixels }
  | (Omit<DriftHarmonizationJob, 'underlying'> & { kind: 'driftHarmonization'; layer: WirePixels; underlying: WirePixels })
  | (AutoCutGeometry & { kind: 'autoCutExtraction'; original: WirePixels; modified: WirePixels });

type WireAutoCutExtraction = Omit<AutoCutExtraction, 'imageData'> & { pixels: WirePixels };

interface OpenCvWorkerRequest {
  messageId: string;
  job: OpenCvJob;
}

type OpenCvWorkerResponse =
  | { messageId: string; error: string }
  | { messageId: string; result: LayerAlignmentResult | WirePixels | WireAutoCutExtraction | null };

// Cold start is fetching and compiling the 11 MB OpenCV chunk, then a 1440² alignment (~0.3 s) or a 16 MP auto cut (~2.2 s):
// 30 s leaves a slow link room yet bounds a worker that hangs without ever raising an error event.
const OPENCV_WORKER_TIMEOUT_MS = 30_000;
// The drift job posts nothing while it runs, and its full-resolution pass on a 16K upscaled layer extrapolates
// to ~25 s: a silence past 30 s would declare it dead and re-run it on the main thread.
const DRIFT_HARMONIZATION_WORKER_TIMEOUT_MS = 90_000;

const handleOpenCvWorkerRequest = async (request: OpenCvWorkerRequest, cv: CV): Promise<{ response: OpenCvWorkerResponse; transfer: Transferable[] }> => {
  try {
    const { job } = request;
    switch (job.kind) {
      case 'layerAlignment':
        return {
          response: {
            messageId: request.messageId,
            result: computeTiledLayerAlignment(
              decodeWorkerPixels('OpenCV', job.edited),
              decodeWorkerPixels('OpenCV', job.original),
              cv,
            ),
          },
          transfer: [],
        };
      case 'driftHarmonization': {
        const { layer, underlying, ...rest } = job;
        const result = await harmonizeLayerDrift(cv, decodeWorkerPixels('OpenCV', layer), { ...rest, underlying: decodeWorkerPixels('OpenCV', underlying) });
        if (!result) return { response: { messageId: request.messageId, result: null }, transfer: [] };
        const buffer = result.data.buffer as ArrayBuffer;
        return { response: { messageId: request.messageId, result: { buffer, width: result.width, height: result.height } }, transfer: [buffer] };
      }
      case 'autoCutExtraction': {
        const { original, modified, ...geometry } = job;
        const result = computeAutoCutExtraction(cv, decodeWorkerPixels('OpenCV', original), decodeWorkerPixels('OpenCV', modified), geometry);
        if (!result) return { response: { messageId: request.messageId, result: null }, transfer: [] };
        const { imageData, ...placement } = result;
        const buffer = imageData.data.buffer as ArrayBuffer;
        return {
          response: { messageId: request.messageId, result: { ...placement, pixels: { buffer, width: imageData.width, height: imageData.height } } },
          transfer: [buffer],
        };
      }
    }
  } catch (error) {
    return { response: { messageId: request.messageId, error: error instanceof Error ? error.message : 'Unknown OpenCV worker error.' }, transfer: [] };
  }
};

/**
 * Worker side: one OpenCV instance for every job kind, jobs posted before it is ready wait for it.
 * An init failure goes to `raise`, which the worker turns into an uncaught error: the browser
 * delivers that as the Worker `error` event, which this client maps to "unavailable". A job that
 * throws only produces an error reply, so it never costs the worker.
 */
export const startOpenCvWorker = (
  cvReady: CV | PromiseLike<CV>,
  post: (message: OpenCvWorkerResponse | OpenCvReadyMessage, transfer?: Transferable[]) => void,
  raise: (error: unknown) => void,
) => {
  const cv = Promise.resolve(cvReady).then((loaded) => {
    const version = /OpenCV (\S+)/.exec(loaded.getBuildInformation())?.[1] ?? 'unknown';
    post({ type: 'ready', version, initMs: performance.now() });
    return loaded;
  });
  cv.catch(raise);
  return (request: OpenCvWorkerRequest) => {
    cv.then((loaded) => handleOpenCvWorkerRequest(request, loaded)).then(({ response, transfer }) => post(response, transfer), raise);
  };
};

const isNumberArray = (value: unknown, length?: number) =>
  Array.isArray(value) && (length === undefined || value.length === length) && value.every((entry) => typeof entry === 'number');

const isStringArray = (value: unknown) => Array.isArray(value) && value.every((entry) => typeof entry === 'string');

/** Every field of the reply is checked, `consensus` included: it is displayed and logged, so a wrong shape shows. */
const isAlignmentResult = (value: unknown): value is LayerAlignmentResult => {
  if (typeof value !== 'object' || value === null) return false;
  const { matrix, refusedFor, consensus } = value as LayerAlignmentResult;
  if (!(matrix === null || isNumberArray(matrix, 6)) || !isStringArray(refusedFor)) return false;
  if (consensus === undefined) return true;
  return typeof consensus === 'object' && consensus !== null
    && typeof consensus.inlierTiles === 'number' && typeof consensus.totalTiles === 'number'
    && isStringArray(consensus.sides) && (consensus.residualRms === null || typeof consensus.residualRms === 'number')
    && typeof consensus.scaleEstimated === 'boolean' && isNumberArray(consensus.fittedMatrix, 6);
};

/** Both images are copied, never transferred: they stay the caller's fallback input. */
export const runLayerAlignmentInWorker = (
  worker: Worker,
  editedLayer: ImageData,
  originalUnderlyingImage: ImageData,
  timeoutMs: number = OPENCV_WORKER_TIMEOUT_MS,
): Promise<LayerAlignmentResult> => {
  const job: OpenCvJob = { kind: 'layerAlignment', edited: copyPixels(editedLayer), original: copyPixels(originalUnderlyingImage) };
  return sendWorkerRequest<Omit<OpenCvWorkerRequest, 'messageId'>, unknown>(
    worker,
    { job },
    [job.edited.buffer, job.original.buffer],
    { label: 'OpenCV', timeoutMs, errorEventMarksUnavailable: true },
  ).then((result) => {
    if (!isAlignmentResult(result)) throw new Error('OpenCV worker returned an invalid alignment result.');
    return result;
  });
};

/** Creates the worker on first use only: a worker dropped as unavailable is never recreated. */
const ensureOpenCvWorker = (workerRef: LazyWorkerRef, createWorker: () => Worker) => {
  if (workerRef.created) return;
  workerRef.created = true;
  try {
    workerRef.current = createWorker();
  } catch (error) {
    console.warn('[OpenCV] Worker could not be created, running on the main thread.', error);
  }
};

export const runLayerAlignmentWithFallback = (
  workerRef: LazyWorkerRef,
  createWorker: () => Worker,
  editedLayer: ImageData,
  originalUnderlyingImage: ImageData,
  mainThreadCv: CV,
  timeoutMs: number = OPENCV_WORKER_TIMEOUT_MS,
) => {
  ensureOpenCvWorker(workerRef, createWorker);
  return runWithWorkerFallback(
    workerRef,
    (worker) => runLayerAlignmentInWorker(worker, editedLayer, originalUnderlyingImage, timeoutMs),
    () => computeTiledLayerAlignment(editedLayer, originalUnderlyingImage, mainThreadCv),
    '[OpenCV] Worker alignment failed, falling back to main thread.',
  );
};

/**
 * The standard Harmonize. Both images are copied, never transferred: the layer stays the undo state and the fallback
 * input. Null is the lib's deterministic refusal: it comes back as a result, so the fallback never re-runs the job
 * on the main thread just to refuse again.
 */
export const runDriftHarmonizationWithFallback = (
  workerRef: LazyWorkerRef,
  createWorker: () => Worker,
  layer: ImageData,
  job: DriftHarmonizationJob,
  mainThreadCv: CV,
  timeoutMs: number = DRIFT_HARMONIZATION_WORKER_TIMEOUT_MS,
) => {
  ensureOpenCvWorker(workerRef, createWorker);
  return runWithWorkerFallback(
    workerRef,
    (worker) => {
      const request: OpenCvJob = { ...job, kind: 'driftHarmonization', layer: copyPixels(layer), underlying: copyPixels(job.underlying) };
      return sendWorkerRequest<Omit<OpenCvWorkerRequest, 'messageId'>, WirePixels | null>(
        worker,
        { job: request },
        [request.layer.buffer, request.underlying.buffer],
        { label: 'OpenCV', timeoutMs, errorEventMarksUnavailable: true },
      ).then((result) => result && decodeWorkerPixels('OpenCV', result));
    },
    () => harmonizeLayerDrift(mainThreadCv, layer, job),
    '[OpenCV] Worker drift harmonization failed, falling back to main thread.',
  );
};

/**
 * Auto Cut. Both renders are copied, never transferred: they stay the fallback input. Null is the lib's own diff
 * failure: it comes back as a result, so the fallback never re-runs the job just to fail again.
 */
export const runAutoCutExtractionWithFallback = (
  workerRef: LazyWorkerRef,
  createWorker: () => Worker,
  original: ImageData,
  modified: ImageData,
  geometry: AutoCutGeometry,
  mainThreadCv: CV,
) => {
  ensureOpenCvWorker(workerRef, createWorker);
  return runWithWorkerFallback(
    workerRef,
    (worker) => {
      const request: OpenCvJob = { ...geometry, kind: 'autoCutExtraction', original: copyPixels(original), modified: copyPixels(modified) };
      return sendWorkerRequest<Omit<OpenCvWorkerRequest, 'messageId'>, WireAutoCutExtraction | null>(
        worker,
        { job: request },
        [request.original.buffer, request.modified.buffer],
        { label: 'OpenCV', timeoutMs: OPENCV_WORKER_TIMEOUT_MS, errorEventMarksUnavailable: true },
      ).then((result): AutoCutExtraction | null => {
        if (!result) return null;
        const { pixels, ...placement } = result;
        return { ...placement, imageData: decodeWorkerPixels('OpenCV', pixels) };
      });
    },
    () => computeAutoCutExtraction(mainThreadCv, original, modified, geometry),
    '[OpenCV] Worker auto cut failed, falling back to main thread.',
  );
};
